import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  List,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CustomizationSection,
  CategoryCustomizationRules,
  RemovableIngredientBinding,
} from '../models/Category';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const sectionTypes = [
  { value: 'included_options', label: 'Options incluses' },
  { value: 'paid_supplements', label: 'Suppléments payants' },
  { value: 'removable_ingredients', label: 'Ingrédients supprimables' },
  { value: 'suggested_products', label: 'Produits suggérés' }
];

const selectionUITypes = [
  { value: 'checkbox', label: 'Cases à cocher (Multiple)' },
  { value: 'radio', label: 'Boutons radio (Unique)' }
];

interface CustomizationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (customizationRules: CategoryCustomizationRules) => Promise<void>;
  category: string;
  categoryName: string;
  existingRules?: CategoryCustomizationRules;
  addonCategories: { id: string; name: string }[];
  productCategories: { id: string; name: string }[];
  productIngredients?: string[];
}

interface SectionFormData {
  sectionId: string;
  type: string;
  title: string;
  minSelection?: number;
  maxSelection?: number;
  addonCategory?: string;
  productCategory?: string;
  selectionUI?: string;
  nonRemovable?: string[];
  ingredientBindings?: RemovableIngredientBinding[];
  disabledWhenRemovedIngredientId?: string;
}

const MATCH_STOP_WORDS = new Set([
  'au',
  'aux',
  'de',
  'des',
  'du',
  'la',
  'le',
  'les',
  'choix',
  'option',
  'options',
  'incluse',
  'incluses',
  'selection',
  'selectionnee',
  'selectionner',
  'selectionne',
  'sauce',
]);

const normalizeText = (value?: string): string =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const buildIngredientBindingId = (label: string, existingIds: Set<string>): string => {
  const slugBase =
    normalizeText(label)
      .split(' ')
      .filter(Boolean)
      .join('_') || 'ingredient';

  let candidate = `ing_${slugBase}`;
  let suffix = 2;
  while (existingIds.has(candidate)) {
    candidate = `ing_${slugBase}_${suffix}`;
    suffix += 1;
  }
  return candidate;
};

const getRemovableIngredientLabels = (
  productIngredients: string[],
  nonRemovable: string[] = []
): string[] => {
  const blocked = new Set(nonRemovable.map((value) => value.trim()));
  return productIngredients.filter(
    (ingredient) => ingredient.trim().length > 0 && !blocked.has(ingredient)
  );
};

const reconcileIngredientBindings = (
  ingredientLabels: string[],
  previousBindings: RemovableIngredientBinding[] = []
): RemovableIngredientBinding[] => {
  const available = previousBindings.map((binding) => ({
    ...binding,
    normalizedLabel: normalizeText(binding.label),
  }));
  const usedIds = new Set<string>();

  return ingredientLabels.map((label) => {
    const normalizedLabel = normalizeText(label);
    const exactBinding = available.find(
      (binding) => !usedIds.has(binding.id) && binding.label === label
    );
    const normalizedBinding =
      exactBinding ||
      available.find(
        (binding) =>
          !usedIds.has(binding.id) && binding.normalizedLabel === normalizedLabel
      );

    if (normalizedBinding) {
      usedIds.add(normalizedBinding.id);
      return {
        id: normalizedBinding.id,
        label,
      };
    }

    const generatedId = buildIngredientBindingId(label, usedIds);
    usedIds.add(generatedId);
    return {
      id: generatedId,
      label,
    };
  });
};

const isSauceChoiceBinding = (binding: RemovableIngredientBinding): boolean => {
  const normalized = normalizeText(binding.label);
  return (
    normalized.includes('sauce') &&
    (normalized.includes('choix') ||
      normalized.includes('choice') ||
      normalized.includes('selection') ||
      normalized.includes('selected'))
  );
};

const extractRelevantTokens = (value: string): string[] =>
  normalizeText(value)
    .split(' ')
    .filter((token) => token.length >= 3 && !MATCH_STOP_WORDS.has(token));

const detectAutoLinkedRemovedIngredientId = (
  section: Pick<SectionFormData, 'title' | 'addonCategory'>,
  removableBindings: RemovableIngredientBinding[],
  addonCategories: { id: string; name: string }[]
): string | null => {
  if (removableBindings.length === 0) {
    return null;
  }

  const addonCategoryName =
    addonCategories.find((item) => item.id === section.addonCategory)?.name || '';
  const combinedSource = `${section.title || ''} ${addonCategoryName}`;
  const normalizedCombined = normalizeText(combinedSource);

  if (normalizedCombined.includes('sauce')) {
    const sauceChoice = removableBindings.find(isSauceChoiceBinding);
    if (sauceChoice) {
      return sauceChoice.id;
    }

    const sauceCandidates = removableBindings.filter((binding) =>
      normalizeText(binding.label).includes('sauce')
    );
    if (sauceCandidates.length === 1) {
      return sauceCandidates[0].id;
    }
  }

  const targetTokens = Array.from(
    new Set([
      ...extractRelevantTokens(section.title || ''),
      ...extractRelevantTokens(addonCategoryName),
    ])
  );

  if (targetTokens.length === 0) {
    return null;
  }

  const scoredBindings = removableBindings
    .map((binding) => {
      const normalizedLabel = normalizeText(binding.label);
      const score = targetTokens.filter((token) => normalizedLabel.includes(token)).length;
      return { binding, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scoredBindings.length === 0) {
    return null;
  }

  if (
    scoredBindings.length === 1 ||
    scoredBindings[0].score > scoredBindings[1].score
  ) {
    return scoredBindings[0].binding.id;
  }

  return null;
};

interface SortableSectionRowProps {
  section: CustomizationSection;
  index: number;
  sectionCount: number;
  renderSectionInfo: (section: CustomizationSection) => string;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onMove: (index: number, direction: -1 | 1) => void;
}

const SortableSectionRow: React.FC<SortableSectionRowProps> = ({
  section,
  index,
  sectionCount,
  renderSectionInfo,
  onEdit,
  onDelete,
  onMove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.sectionId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 1,
        mb: 1,
        border: '1px solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: isDragging ? 4 : 0,
      }}
    >
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        aria-label="Déplacer la section"
        sx={{ cursor: isDragging ? 'grabbing' : 'grab', color: 'text.secondary' }}
      >
        <DragIndicatorIcon />
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {index + 1}. {section.title || 'Section sans titre'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
          <Chip
            label={sectionTypes.find(t => t.value === section.type)?.label || section.type}
            size="small"
          />
          <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
            {renderSectionInfo(section)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <IconButton
          size="small"
          onClick={() => onMove(index, -1)}
          disabled={index === 0}
          aria-label="Monter la section"
        >
          <KeyboardArrowUpIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onMove(index, 1)}
          disabled={index === sectionCount - 1}
          aria-label="Descendre la section"
        >
          <KeyboardArrowDownIcon />
        </IconButton>
        <IconButton size="small" onClick={() => onEdit(index)} aria-label="Modifier la section">
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(index)} aria-label="Supprimer la section">
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const CustomizationDialog: React.FC<CustomizationDialogProps> = ({
  open,
  onClose,
  onSave,
  category,
  categoryName,
  existingRules,
  addonCategories,
  productCategories,
  productIngredients = []
}) => {
  // État pour les sections de personnalisation
  const [sections, setSections] = useState<CustomizationSection[]>([]);
  
  // État pour l'édition d'une section spécifique
  const [editingSection, setEditingSection] = useState<SectionFormData | null>(null);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [showSectionForm, setShowSectionForm] = useState(false);
  
  // État pour la soumission et les erreurs
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const dragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getResolvedRemovableBindings = (
    sourceSections: Array<CustomizationSection | SectionFormData> = sections
  ): RemovableIngredientBinding[] => {
    const bindingMap = new Map<string, RemovableIngredientBinding>();

    sourceSections.forEach((section) => {
      if (section.type !== 'removable_ingredients') {
        return;
      }

      const removableLabels = getRemovableIngredientLabels(
        productIngredients,
        section.nonRemovable || []
      );
      const bindings = reconcileIngredientBindings(
        removableLabels,
        section.ingredientBindings || []
      );

      bindings.forEach((binding) => {
        bindingMap.set(binding.id, binding);
      });
    });

    return Array.from(bindingMap.values());
  };

  const findRemovedIngredientLabel = (bindingId?: string): string | null => {
    if (!bindingId) {
      return null;
    }

    return (
      getResolvedRemovableBindings().find((binding) => binding.id === bindingId)?.label ||
      null
    );
  };

  const removableBindingsForForm = getResolvedRemovableBindings();
  const autoDetectedRemovedIngredientId =
    editingSection?.type === 'included_options'
      ? detectAutoLinkedRemovedIngredientId(
          editingSection,
          removableBindingsForForm,
          addonCategories
        )
      : null;
  const autoDetectedRemovedIngredientLabel =
    removableBindingsForForm.find(
      (binding) => binding.id === autoDetectedRemovedIngredientId
    )?.label || null;
  const selectedRemovedIngredientIdForForm =
    editingSection?.type === 'included_options' &&
    editingSection.disabledWhenRemovedIngredientId &&
    removableBindingsForForm.some(
      (binding) => binding.id === editingSection.disabledWhenRemovedIngredientId
    )
      ? editingSection.disabledWhenRemovedIngredientId
      : '';

  // Initialiser l'état avec les données existantes
  useEffect(() => {
    if (existingRules && existingRules.sections) {
      setSections([...existingRules.sections]);
    } else {
      setSections([]);
    }
    
    // Réinitialiser les états d'édition et d'erreur
    setEditingSection(null);
    setEditingSectionIndex(null);
    setShowSectionForm(false);
    setError(null);
    setValidationErrors({});
  }, [existingRules, open]);

  useEffect(() => {
    if (
      !showSectionForm ||
      !editingSection ||
      editingSection.type !== 'included_options' ||
      !autoDetectedRemovedIngredientId
    ) {
      return;
    }

    const currentId = editingSection.disabledWhenRemovedIngredientId;
    const hasManualChoice = currentId !== undefined;
    const currentIsValid =
      !!currentId &&
      removableBindingsForForm.some((binding) => binding.id === currentId);

    if (currentIsValid || hasManualChoice) {
      return;
    }

    setEditingSection((prev) => {
      if (!prev || prev.type !== 'included_options') {
        return prev;
      }
      if (prev.disabledWhenRemovedIngredientId !== undefined) {
        return prev;
      }
      return {
        ...prev,
        disabledWhenRemovedIngredientId: autoDetectedRemovedIngredientId,
      };
    });
  }, [
    autoDetectedRemovedIngredientId,
    editingSection,
    removableBindingsForForm,
    showSectionForm,
  ]);

  // Fonctions de gestion des sections
  const handleAddSection = () => {
    // Générer un ID unique pour la nouvelle section
    const newSectionId = `${category}_section_${Date.now()}`;
    
    // Initialiser une nouvelle section avec des valeurs par défaut
    setEditingSection({
      sectionId: newSectionId,
      type: 'included_options',
      title: '',
      minSelection: undefined,
      maxSelection: undefined,
      addonCategory: undefined,
      productCategory: undefined,
      selectionUI: 'checkbox',
      nonRemovable: [],
      ingredientBindings: [],
      disabledWhenRemovedIngredientId: undefined,
    });
    
    setEditingSectionIndex(null);
    setShowSectionForm(true);
  };

  const handleEditSection = (index: number) => {
    const section = sections[index];
    setEditingSection({
      ...section,
      minSelection: section.minSelection,
      maxSelection: section.maxSelection,
      addonCategory: section.addonCategory,
      productCategory: section.productCategory,
      selectionUI: section.selectionUI,
      nonRemovable: section.nonRemovable || [],
      ingredientBindings: section.ingredientBindings || [],
      disabledWhenRemovedIngredientId: section.disabledWhenRemovedIngredientId,
    });
    setEditingSectionIndex(index);
    setShowSectionForm(true);
  };

  const handleDeleteSection = (index: number) => {
    const updatedSections = [...sections];
    updatedSections.splice(index, 1);
    setSections(updatedSections);
  };

  const handleMoveSection = (index: number, direction: -1 | 1) => {
    setSections((currentSections) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= currentSections.length) {
        return currentSections;
      }
      return arrayMove(currentSections, index, nextIndex);
    });
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    setSections((currentSections) => {
      const oldIndex = currentSections.findIndex((section) => section.sectionId === active.id);
      const newIndex = currentSections.findIndex((section) => section.sectionId === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return currentSections;
      }
      return arrayMove(currentSections, oldIndex, newIndex);
    });
  };

  const handleCancelSectionEdit = () => {
    setEditingSection(null);
    setEditingSectionIndex(null);
    setShowSectionForm(false);
    setValidationErrors({});
  };

  // Validation de la section
  const validateSectionForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!editingSection) return false;
    
    if (!editingSection.title.trim()) {
      errors.title = 'Le titre est requis';
    }
    
    if (editingSection.type === 'included_options' || editingSection.type === 'paid_supplements') {
      // Pour les types qui nécessitent une catégorie d'addons
      if (!editingSection.addonCategory) {
        errors.addonCategory = 'Veuillez sélectionner une catégorie d\'addons';
      }
      
      // Vérifier que min est <= max si les deux sont définis
      if (editingSection.minSelection !== undefined && 
          editingSection.maxSelection !== undefined && 
          editingSection.minSelection > editingSection.maxSelection) {
        errors.minSelection = 'La sélection minimale doit être inférieure ou égale à la sélection maximale';
      }
      
      // Si c'est un bouton radio, max doit être 1
      if (editingSection.selectionUI === 'radio' && editingSection.maxSelection !== 1) {
        errors.selectionUI = 'Pour les boutons radio, la sélection maximale doit être 1';
      }
    } else if (editingSection.type === 'suggested_products') {
      // Pour les suggestions de produits
      if (!editingSection.productCategory) {
        errors.productCategory = 'Veuillez sélectionner une catégorie de produits';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Sauvegarde de la section en cours d'édition
  const handleSaveSectionEdit = () => {
    if (!validateSectionForm() || !editingSection) return;
    
    // Créer une nouvelle section à partir des données du formulaire
    const newSection: CustomizationSection = {
      sectionId: editingSection.sectionId,
      type: editingSection.type as any,
      title: editingSection.title
    };
    
    // Ajouter les propriétés optionnelles EN FONCTION DU TYPE DE SECTION
    if (editingSection.type === 'included_options' || editingSection.type === 'paid_supplements') {
      // Pour les options incluses et suppléments payants
      if (editingSection.minSelection !== undefined) {
        newSection.minSelection = editingSection.minSelection;
      }
      
      if (editingSection.maxSelection !== undefined) {
        newSection.maxSelection = editingSection.maxSelection;
      }
      
      if (editingSection.addonCategory) {
        newSection.addonCategory = editingSection.addonCategory;
      }
      
      if (editingSection.selectionUI) {
        newSection.selectionUI = editingSection.selectionUI as any;
      }

      if (
        editingSection.type === 'included_options' &&
        editingSection.disabledWhenRemovedIngredientId &&
        removableBindingsForForm.some(
          (binding) => binding.id === editingSection.disabledWhenRemovedIngredientId
        )
      ) {
        newSection.disabledWhenRemovedIngredientId =
          editingSection.disabledWhenRemovedIngredientId;
      }
    } else if (editingSection.type === 'suggested_products') {
      // Pour les produits suggérés
      if (editingSection.productCategory) {
        newSection.productCategory = editingSection.productCategory;
      }
    } else if (editingSection.type === 'removable_ingredients') {
      // Pour les ingrédients supprimables - conserver selectionUI (par défaut checkbox) et nonRemovable
      // Si selectionUI non défini dans le form, on force 'checkbox'
      newSection.selectionUI = (editingSection.selectionUI as any) || 'checkbox';
      if (editingSection.nonRemovable && editingSection.nonRemovable.length > 0) {
        newSection.nonRemovable = editingSection.nonRemovable;
      }
      const removableLabels = getRemovableIngredientLabels(
        productIngredients,
        editingSection.nonRemovable || []
      );
      newSection.ingredientBindings = reconcileIngredientBindings(
        removableLabels,
        editingSection.ingredientBindings || []
      );
    }
    
    // Mettre à jour la liste des sections
    const updatedSections = [...sections];
    if (editingSectionIndex !== null) {
      updatedSections[editingSectionIndex] = newSection;
    } else {
      updatedSections.push(newSection);
    }

    const normalizedSections = updatedSections.map((section) => {
      if (section.type !== 'removable_ingredients') {
        return section;
      }

      const removableLabels = getRemovableIngredientLabels(
        productIngredients,
        section.nonRemovable || []
      );

      return {
        ...section,
        ingredientBindings: reconcileIngredientBindings(
          removableLabels,
          section.ingredientBindings || []
        ),
      };
    });

    const validRemovedIngredientIds = new Set(
      getResolvedRemovableBindings(normalizedSections).map((binding) => binding.id)
    );

    const sanitizedSections = normalizedSections.map((section) => {
      if (
        section.type === 'included_options' &&
        section.disabledWhenRemovedIngredientId &&
        !validRemovedIngredientIds.has(section.disabledWhenRemovedIngredientId)
      ) {
        const { disabledWhenRemovedIngredientId, ...rest } = section;
        void disabledWhenRemovedIngredientId;
        return rest;
      }
      return section;
    });
    
    setSections(sanitizedSections);
    setShowSectionForm(false);
    setEditingSection(null);
    setEditingSectionIndex(null);
  };

  // Soumission du formulaire complet
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      // Créer l'objet de règles de personnalisation
      const customizationRules: CategoryCustomizationRules = {
        category,
        sections
      };
      
      // Appeler la fonction de sauvegarde fournie par le parent
      await onSave(customizationRules);
      onClose();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde des règles de personnalisation:', err);
      setError('Une erreur est survenue lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction d'aide pour le rendu des infos supplémentaires d'une section
  const renderSectionInfo = (section: CustomizationSection) => {
    let infos = [];
    
    if (section.type === 'included_options' || section.type === 'paid_supplements') {
      // Trouver le nom de la catégorie d'addons
      const addonCategory = addonCategories.find(cat => cat.id === section.addonCategory);
      if (addonCategory) {
        infos.push(`Addons: ${addonCategory.name}`);
      }
      
      // Afficher les limites de sélection
      if (section.minSelection !== undefined || section.maxSelection !== undefined) {
        let selectionText = 'Sélection: ';
        if (section.minSelection !== undefined && section.maxSelection !== undefined) {
          if (section.minSelection === section.maxSelection) {
            selectionText += `exactement ${section.minSelection}`;
          } else {
            selectionText += `${section.minSelection} à ${section.maxSelection}`;
          }
        } else if (section.minSelection !== undefined) {
          selectionText += `min ${section.minSelection}`;
        } else if (section.maxSelection !== undefined) {
          selectionText += `max ${section.maxSelection}`;
        }
        infos.push(selectionText);
      }
      
      // Afficher le type d'UI
      if (section.selectionUI) {
        infos.push(`UI: ${section.selectionUI === 'checkbox' ? 'Cases à cocher' : 'Boutons radio'}`);
      }

      if (section.type === 'included_options' && section.disabledWhenRemovedIngredientId) {
        const linkedLabel = findRemovedIngredientLabel(section.disabledWhenRemovedIngredientId);
        if (linkedLabel) {
          infos.push(`Ignorée si retiré: ${linkedLabel}`);
        }
      }
    } else if (section.type === 'suggested_products') {
      // Trouver le nom de la catégorie de produits
      const productCategory = productCategories.find(cat => cat.id === section.productCategory);
      if (productCategory) {
        infos.push(`Produits: ${productCategory.name}`);
      }
    } else if (section.type === 'removable_ingredients') {
      if (section.nonRemovable && section.nonRemovable.length > 0) {
        infos.push(`${section.nonRemovable.length} ingrédients non supprimables`);
      }
      if (section.ingredientBindings && section.ingredientBindings.length > 0) {
        infos.push(`${section.ingredientBindings.length} ingrédients liés`);
      }
      // Afficher le type d'UI (forcé à 'checkbox' si absent)
      if (section.selectionUI) {
        infos.push(`UI: ${section.selectionUI === 'checkbox' ? 'Cases à cocher' : 'Boutons radio'}`);
      } else {
        infos.push('UI: Cases à cocher');
      }
    }
    
    return infos.join(' • ');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Règles de personnalisation pour {categoryName}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!showSectionForm ? (
          // Liste des sections existantes
          <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1">
                Sections de personnalisation ({sections.length})
              </Typography>
              
              <Button 
                variant="outlined" 
                startIcon={<AddIcon />}
                onClick={handleAddSection}
              >
                Ajouter une section
              </Button>
            </Box>
            
            {sections.length === 0 ? (
              <Alert severity="info">
                Aucune section de personnalisation définie pour cette catégorie.
              </Alert>
            ) : (
              <DndContext
                sensors={dragSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
              >
                <SortableContext
                  items={sections.map((section) => section.sectionId)}
                  strategy={verticalListSortingStrategy}
                >
                  <List disablePadding>
                    {sections.map((section, index) => (
                      <SortableSectionRow
                        key={section.sectionId}
                        section={section}
                        index={index}
                        sectionCount={sections.length}
                        renderSectionInfo={renderSectionInfo}
                        onEdit={handleEditSection}
                        onDelete={handleDeleteSection}
                        onMove={handleMoveSection}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            )}
          </Box>
        ) : (
          // Formulaire d'édition d'une section
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {editingSectionIndex !== null ? 'Modifier la section' : 'Ajouter une section'}
            </Typography>
            
            <TextField
              fullWidth
              label="Titre de la section"
              value={editingSection?.title || ''}
              onChange={(e) => setEditingSection({...editingSection!, title: e.target.value})}
              margin="normal"
              error={!!validationErrors.title}
              helperText={validationErrors.title}
              required
            />

            <FormControl 
              fullWidth 
              margin="normal"
              required
            >
              <InputLabel>Type de section</InputLabel>
              <Select
                value={editingSection?.type || ''}
                onChange={(e) => setEditingSection({...editingSection!, type: e.target.value})}
                label="Type de section"
              >
                {sectionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Champs spécifiques au type de section */}
            {(editingSection?.type === 'included_options' || editingSection?.type === 'paid_supplements') && (
              <>
                <FormControl 
                  fullWidth 
                  margin="normal"
                  error={!!validationErrors.addonCategory}
                  required
                >
                  <InputLabel>Catégorie d'addons</InputLabel>
                  <Select
                    value={editingSection.addonCategory || ''}
                    onChange={(e) => setEditingSection({...editingSection, addonCategory: e.target.value})}
                    label="Catégorie d'addons"
                  >
                    {addonCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.addonCategory && (
                    <FormHelperText>{validationErrors.addonCategory}</FormHelperText>
                  )}
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Sélection minimale"
                    type="number"
                    value={editingSection.minSelection !== undefined ? editingSection.minSelection : ''}
                    onChange={(e) => setEditingSection({
                      ...editingSection, 
                      minSelection: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    inputProps={{ min: 0 }}
                    sx={{ flex: 1 }}
                    error={!!validationErrors.minSelection}
                    helperText={validationErrors.minSelection}
                  />
                  
                  <TextField
                    label="Sélection maximale"
                    type="number"
                    value={editingSection.maxSelection !== undefined ? editingSection.maxSelection : ''}
                    onChange={(e) => setEditingSection({
                      ...editingSection, 
                      maxSelection: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                    error={!!validationErrors.maxSelection}
                    helperText={validationErrors.maxSelection}
                  />
                </Box>

                <FormControl 
                  fullWidth 
                  margin="normal"
                  error={!!validationErrors.selectionUI}
                >
                  <InputLabel>Type d'interface</InputLabel>
                  <Select
                    value={editingSection.selectionUI || 'checkbox'}
                    onChange={(e) => setEditingSection({...editingSection, selectionUI: e.target.value})}
                    label="Type d'interface"
                  >
                    {selectionUITypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.selectionUI && (
                    <FormHelperText>{validationErrors.selectionUI}</FormHelperText>
                  )}
                </FormControl>

                {editingSection?.type === 'included_options' && (
                  removableBindingsForForm.length > 0 ? (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Ignorer si ingrédient retiré</InputLabel>
                      <Select
                        value={selectedRemovedIngredientIdForForm}
                        onChange={(e) =>
                          setEditingSection({
                            ...editingSection,
                            disabledWhenRemovedIngredientId:
                              e.target.value || '',
                          })
                        }
                        label="Ignorer si ingrédient retiré"
                      >
                        <MenuItem value="">
                          <em>Aucune dépendance</em>
                        </MenuItem>
                        {removableBindingsForForm.map((binding) => (
                          <MenuItem key={binding.id} value={binding.id}>
                            {binding.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {editingSection.disabledWhenRemovedIngredientId &&
                        autoDetectedRemovedIngredientId ===
                          editingSection.disabledWhenRemovedIngredientId &&
                        autoDetectedRemovedIngredientLabel
                          ? `Association détectée automatiquement avec "${autoDetectedRemovedIngredientLabel}".`
                          : 'Si cet ingrédient est retiré dans "Ingrédients supprimables", cette section ne sera plus obligatoire.'}
                      </FormHelperText>
                    </FormControl>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Ajoutez d&apos;abord une section "Ingrédients supprimables" pour pouvoir lier cette section à un ingrédient retiré.
                    </Alert>
                  )
                )}
              </>
            )}

            {editingSection?.type === 'suggested_products' && (
              <FormControl 
                fullWidth 
                margin="normal"
                error={!!validationErrors.productCategory}
                required
              >
                <InputLabel>Catégorie de produits</InputLabel>
                <Select
                  value={editingSection.productCategory || ''}
                  onChange={(e) => setEditingSection({...editingSection, productCategory: e.target.value})}
                  label="Catégorie de produits"
                >
                  {productCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.productCategory && (
                  <FormHelperText>{validationErrors.productCategory}</FormHelperText>
                )}
              </FormControl>
            )}

            {editingSection?.type === 'removable_ingredients' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Sélectionnez les ingrédients qui ne sont pas supprimables :</Typography>
                {productIngredients.map((ing) => (
                  <FormControlLabel
                    key={ing}
                    control={
                      <Checkbox
                        checked={editingSection.nonRemovable?.includes(ing) || false}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...(editingSection.nonRemovable || []), ing]
                            : (editingSection.nonRemovable || []).filter(i => i !== ing);
                          setEditingSection({ ...editingSection, nonRemovable: updated });
                        }}
                      />
                    }
                    label={ing}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {showSectionForm ? (
          // Actions pour le formulaire d'édition de section
          <>
            <Button onClick={handleCancelSectionEdit}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveSectionEdit} 
              variant="contained" 
              color="primary"
            >
              Enregistrer la section
            </Button>
          </>
        ) : (
          // Actions pour le dialogue principal
          <>
            <Button onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={submitting}
            >
              {submitting ? 'Enregistrement...' : 'Enregistrer les règles'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomizationDialog;
